import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Card, Badge, ErrorState, LoadingSpinner } from '../../components/ui';
import { useApiQuery } from '../../hooks/useApi';
import { useOrdenesCargaEnabled } from '../../hooks/useOrdenesCargaEnabled';
import ordenesCargaCombustibleService from '../../services/ordenesCargaCombustibleService';
import { formatDateSafe } from '../../utils/dateUtils';
import type { EstadoOrdenCarga, EstadoDestino, OrdenCargaCombustible, OrdenCargaDestino } from '../../types/ordenesCargaCombustible';

const badgeVariantPorEstado: Record<EstadoOrdenCarga, 'gray' | 'yellow' | 'green' | 'red' | 'blue'> = {
  SOLICITADA: 'blue',
  AUTORIZADA: 'yellow',
  PENDIENTE: 'gray',
  APROBADA: 'green',
  RECHAZADA: 'red',
};

const badgeVariantPorEstadoDestino: Record<EstadoDestino, 'gray' | 'yellow' | 'green' | 'red' | 'blue'> = {
  PENDIENTE: 'gray',
  CARGADA: 'yellow',
  APROBADA: 'green',
  RECHAZADA: 'red',
};

// Ver el mismo comentario en OrdenesCargaCombustibleView.tsx: el badge de
// estado del header solo aporta algo que las líneas de destino no muestran
// ya en SOLICITADA o en RECHAZADA-antes-de-emitir.
const estadoHeaderEsRedundante = (orden: OrdenCargaCombustible) => {
  if (orden.estado === 'SOLICITADA' || orden.estado === 'AUTORIZADA') return false;
  if (orden.estado === 'RECHAZADA' && orden.destinos.every((d) => d.estado === 'PENDIENTE')) return false;
  return true;
};

export const OrdenesCargaCombustibleMovilView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { habilitado, cargando: cargandoConfig } = useOrdenesCargaEnabled();

  const { data, isLoading } = useApiQuery(
    ['ordenes-carga-mias', 1],
    () => ordenesCargaCombustibleService.mias(undefined, 1, 50),
    { enabled: habilitado }
  );

  if (!cargandoConfig && !habilitado) {
    return <ErrorState message={t('ordenes_carga.module_disabled')} />;
  }

  const ordenes = data?.items || [];

  const destinoTexto = (destino: OrdenCargaDestino) => destino.vehiculo
    ? `🚚 ${destino.vehiculo.patente}`
    : destino.maquina
      ? `⚙️ ${destino.maquina.codigo}`
      : '-';

  const destinoCantidad = (destino: OrdenCargaDestino, unidad: string) =>
    destino.cantidad_autorizada != null
      ? `${destino.cantidad_autorizada} ${unidad}`
      : destino.cantidad_solicitada != null
        ? `(${destino.cantidad_solicitada} ${t('ordenes_carga.solicitar.cantidad_deseada').toLowerCase()})`
        : '-';

  const handleDescargarPdf = async (e: React.MouseEvent, ordenId: number, numeroOrden: string | null) => {
    e.stopPropagation();
    try {
      await ordenesCargaCombustibleService.descargarPdf(ordenId, numeroOrden);
    } catch (error: any) {
      toast.error(error?.message || t('ordenes_carga.messages.error_pdf'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header sticky */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <h1 className="text-lg font-bold text-gray-900">{t('ordenes_carga.title')}</h1>
        <p className="text-xs text-gray-600">{t('ordenes_carga.subtitle')}</p>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : ordenes.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">{t('ordenes_carga.no_pendientes')}</p>
        ) : (
          ordenes.map((orden) => (
            <Card key={orden.id} className="bg-white">
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {orden.numero_orden || t('ordenes_carga.table.numero_orden')}
                  </span>
                  {!estadoHeaderEsRedundante(orden) && (
                    <Badge variant={badgeVariantPorEstado[orden.estado]}>
                      {t(`ordenes_carga.estado.${orden.estado}`)}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-2">{formatDateSafe(orden.fecha_emision)}</div>

                <div className="space-y-2">
                  {orden.destinos.map((destino) => (
                    <div key={destino.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                      <div className="text-xs text-gray-700">
                        <div>{destinoTexto(destino)} · {destinoCantidad(destino, orden.unidad)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={badgeVariantPorEstadoDestino[destino.estado]}>
                          {t(`ordenes_carga.estado_destino.${destino.estado}`)}
                        </Badge>
                        {orden.estado === 'PENDIENTE' && destino.estado === 'PENDIENTE' && (
                          <button
                            onClick={() => navigate(`/ordenes-carga-combustible/${orden.id}/cargar/${destino.id}`)}
                            className="text-xs font-medium text-green-700"
                          >
                            {t('ordenes_carga.actions.cargar')} →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {orden.numero_orden && (
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={(e) => handleDescargarPdf(e, orden.id, orden.numero_orden)}
                      className="text-xs font-medium text-blue-700 underline"
                    >
                      {t('ordenes_carga.actions.descargar_pdf')}
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Botón fijo inferior, encima del MobileNavigation */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-[9999] shadow-lg safe-area-bottom">
        <button
          onClick={() => navigate('/ordenes-carga-combustible/solicitar')}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition-colors"
        >
          {t('ordenes_carga.actions.solicitar')}
        </button>
      </div>
    </div>
  );
};

export default OrdenesCargaCombustibleMovilView;
